import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryTests } from '@/features/tests/data/tests';
import { fetchAllQueryPages } from '@/shared/data/fetchAllQueryPages';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { buildInFilter, mergeFilters, type GridFilters } from '@/ui/composites/grid/gridFilters';
import type {
  TableDropdownOption,
  TableStatusOption,
} from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { TestListItemDto, TestRunStatus } from '@flowqa/shared';

type CountMap = Partial<Record<string, number>>;

type TestsPageFilterOptionCountArgs = {
  enabled: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  search: string;
  selectedStatuses: TestRunStatus[];
  selectedTests: string[];
  selectedSuites: string[];
  selectedTags: string[];
  startDay: string | null;
  endDay: string | null;
  testOptions?: TableDropdownOption[];
  suiteOptions?: TableDropdownOption[];
  statusOptions?: Array<TableStatusOption<TestRunStatus>>;
  tagOptions?: TableDropdownOption[];
};

type CountedTestsPageFilterOptions = {
  countedTestOptions: TableDropdownOption[];
  countedSuiteOptions: TableDropdownOption[];
  countedStatusOptions: Array<TableStatusOption<TestRunStatus>>;
  countedTagOptions: TableDropdownOption[];
};

const sumStatusCounts = (counts: Partial<Record<string, number>> | undefined): number =>
  Object.values(counts ?? {}).reduce<number>((sum, value) => {
    if (value === undefined) return sum;
    return sum + value;
  }, 0);

const sumExecutionStatusCounts = (counts: Partial<Record<string, number>> | undefined): number => {
  const total = counts?.total;
  if (typeof total === 'number') return total;
  return sumStatusCounts(counts);
};

export const buildTestExecutionCountMap = (tests: readonly TestListItemDto[]): CountMap =>
  Object.fromEntries(
    tests.map((test) => [test.id, sumExecutionStatusCounts(test.executionStatusCounts)]),
  ) as CountMap;

export const buildSuiteExecutionCountMapFromTests = (
  tests: readonly TestListItemDto[],
): CountMap => {
  const counts: CountMap = {};
  tests.forEach((test) => {
    const testCount = sumExecutionStatusCounts(test.executionStatusCounts);
    (test.suites?.ids ?? []).forEach((suiteId) => {
      counts[suiteId] = (counts[suiteId] ?? 0) + testCount;
    });
  });
  return counts;
};

export const buildExecutionStatusCountMap = (tests: readonly TestListItemDto[]): CountMap => {
  const counts: CountMap = {};
  tests.forEach((test) => {
    Object.entries(test.executionStatusCounts ?? {}).forEach(([status, value]) => {
      if (status === 'total') return;
      counts[status] = (counts[status] ?? 0) + value;
    });
  });
  return counts;
};

export const buildTagExecutionCountMap = (tests: readonly TestListItemDto[]): CountMap => {
  const counts: CountMap = {};
  tests.forEach((test) => {
    const testCount = sumExecutionStatusCounts(test.executionStatusCounts);
    (test.tags ?? []).forEach((tag) => {
      counts[tag] = (counts[tag] ?? 0) + testCount;
    });
  });
  return counts;
};

const applyCountsToOptions = (
  options: readonly TableDropdownOption[],
  counts: CountMap | undefined,
): TableDropdownOption[] => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  return options.map((option) => ({
    ...option,
    count: counts[option.value] ?? 0,
  }));
};

const applyCountsToStatusOptions = (
  options: ReadonlyArray<TableStatusOption<TestRunStatus>>,
  counts: CountMap | undefined,
): Array<TableStatusOption<TestRunStatus>> => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  const hasErroredOption = options.some((option) => option.value === 'errored');
  return options.map((option) => {
    const groupedErroredCount =
      option.value === 'failed' && !hasErroredOption ? (counts.errored ?? 0) : 0;
    return {
      ...option,
      count: (counts[option.value] ?? 0) + groupedErroredCount,
    };
  });
};

const fetchFilteredTestsForCounts = async ({
  selectedProjectId,
  selectedOrganisationId,
  search,
  filters,
  startDay,
  endDay,
}: {
  selectedProjectId: string;
  selectedOrganisationId: string | null;
  search: string;
  filters: GridFilters;
  startDay: string | null;
  endDay: string | null;
}): Promise<TestListItemDto[]> => {
  const result = await fetchAllQueryPages<TestListItemDto>(async ({ page, pageSize }) => {
    return await queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
      page,
      pageSize,
      sorts: [{ field: 'createdAt', direction: 'desc' }],
      filters,
      search: search.trim() || undefined,
      metricsStartDay: startDay ?? undefined,
      metricsEndDay: endDay ?? undefined,
      include: {
        suites: { ids: true },
        executionStatusCounts: true,
      },
    });
  });
  return result.items;
};

export const useTestsPageFilterOptionCounts = ({
  enabled,
  selectedProjectId,
  selectedOrganisationId,
  search,
  selectedStatuses,
  selectedTests,
  selectedSuites,
  selectedTags,
  startDay,
  endDay,
  testOptions = [],
  suiteOptions = [],
  statusOptions = [],
  tagOptions = [],
}: TestsPageFilterOptionCountArgs): CountedTestsPageFilterOptions => {
  const testOptionCountKey = useMemo(
    () =>
      JSON.stringify({
        search,
        selectedStatuses,
        selectedSuites,
        selectedTags,
        startDay,
        endDay,
      }),
    [endDay, search, selectedStatuses, selectedSuites, selectedTags, startDay],
  );
  const suiteOptionCountKey = useMemo(
    () =>
      JSON.stringify({
        search,
        selectedStatuses,
        selectedTests,
        selectedTags,
        startDay,
        endDay,
      }),
    [endDay, search, selectedStatuses, selectedTags, selectedTests, startDay],
  );
  const statusOptionCountKey = useMemo(
    () =>
      JSON.stringify({
        search,
        selectedTests,
        selectedSuites,
        selectedTags,
        startDay,
        endDay,
      }),
    [endDay, search, selectedSuites, selectedTags, selectedTests, startDay],
  );
  const tagOptionCountKey = useMemo(
    () =>
      JSON.stringify({
        search,
        selectedStatuses,
        selectedTests,
        selectedSuites,
        startDay,
        endDay,
      }),
    [endDay, search, selectedStatuses, selectedSuites, selectedTests, startDay],
  );
  const isEnabled = enabled && selectedProjectId !== null;
  const testCountQuery = useQuery<CountMap>({
    queryKey: queryKeys.dataGrid(
      gridPaths.tests,
      'filter-option-counts',
      'tests',
      selectedOrganisationId,
      selectedProjectId,
      testOptionCountKey,
    ),
    enabled: isEnabled,
    queryFn: async (): Promise<CountMap> => {
      if (selectedProjectId === null) return {};
      const tests = await fetchFilteredTestsForCounts({
        selectedProjectId,
        selectedOrganisationId,
        search,
        filters: mergeFilters(
          buildInFilter('executionStatus', selectedStatuses),
          buildInFilter('suiteId', selectedSuites),
          buildInFilter('tags', selectedTags),
        ),
        startDay,
        endDay,
      });
      return buildTestExecutionCountMap(tests);
    },
    staleTime: 30_000,
  });
  const suiteCountQuery = useQuery<CountMap>({
    queryKey: queryKeys.dataGrid(
      gridPaths.tests,
      'filter-option-counts',
      'suites',
      selectedOrganisationId,
      selectedProjectId,
      suiteOptionCountKey,
    ),
    enabled: isEnabled,
    queryFn: async (): Promise<CountMap> => {
      if (selectedProjectId === null) return {};
      const tests = await fetchFilteredTestsForCounts({
        selectedProjectId,
        selectedOrganisationId,
        search,
        filters: mergeFilters(
          buildInFilter('executionStatus', selectedStatuses),
          buildInFilter('id', selectedTests),
          buildInFilter('tags', selectedTags),
        ),
        startDay,
        endDay,
      });
      return buildSuiteExecutionCountMapFromTests(tests);
    },
    staleTime: 30_000,
  });
  const statusCountQuery = useQuery<CountMap>({
    queryKey: queryKeys.dataGrid(
      gridPaths.tests,
      'filter-option-counts',
      'statuses',
      selectedOrganisationId,
      selectedProjectId,
      statusOptionCountKey,
    ),
    enabled: isEnabled,
    queryFn: async (): Promise<CountMap> => {
      if (selectedProjectId === null) return {};
      const tests = await fetchFilteredTestsForCounts({
        selectedProjectId,
        selectedOrganisationId,
        search,
        filters: mergeFilters(
          buildInFilter('id', selectedTests),
          buildInFilter('suiteId', selectedSuites),
          buildInFilter('tags', selectedTags),
        ),
        startDay,
        endDay,
      });
      return buildExecutionStatusCountMap(tests);
    },
    staleTime: 30_000,
  });
  const tagCountQuery = useQuery<CountMap>({
    queryKey: queryKeys.dataGrid(
      gridPaths.tests,
      'filter-option-counts',
      'tags',
      selectedOrganisationId,
      selectedProjectId,
      tagOptionCountKey,
    ),
    enabled: isEnabled && tagOptions.length > 0,
    queryFn: async (): Promise<CountMap> => {
      if (selectedProjectId === null) return {};
      const tests = await fetchFilteredTestsForCounts({
        selectedProjectId,
        selectedOrganisationId,
        search,
        filters: mergeFilters(
          buildInFilter('executionStatus', selectedStatuses),
          buildInFilter('id', selectedTests),
          buildInFilter('suiteId', selectedSuites),
        ),
        startDay,
        endDay,
      });
      return buildTagExecutionCountMap(tests);
    },
    staleTime: 30_000,
  });

  return {
    countedTestOptions: applyCountsToOptions(testOptions, testCountQuery.data),
    countedSuiteOptions: applyCountsToOptions(suiteOptions, suiteCountQuery.data),
    countedStatusOptions: applyCountsToStatusOptions(statusOptions, statusCountQuery.data),
    countedTagOptions: applyCountsToOptions(tagOptions, tagCountQuery.data),
  };
};
